import { MarkerBoundaryModel, MarkerCollectionModel, MarkerCompetencyModel } from '@/models/marker/marker';
import {
    MarkerCompetenciesModel, MarkerDomainsModel,
    MarkerListModel, MarkerSubjectModel, MarkerTopicModel,
} from '@/models/marker/marker-list';
import {
    RouteLessonsModel, RouteMilestoneModel,
    RouteSignatureModel, RouteSuggestionModel, SuggestionContentModel, SuggestionsModel,
} from '@/models/marker/reroute';
import { Domain } from '@/models/proficiency/domain-topic/domain';
import { sessionService } from '@/providers/services/auth/session';
import { DEFAULT_IMAGES_PATH, MARKER_TYPES } from '@/utils/constants';
import { sortByProperty } from '@/utils/utils';
import { taxonomySerializer } from '../content/taxonomy';

export class MarkerSerializer {

    static get instance() {
        return this.INSTANCE;
    }

    private static INSTANCE = new MarkerSerializer();

    public normalizeMarkerSearchData(payload: any) {
        const markers = payload.markers || [];
        const markerList: MarkerListModel[] = [];
        const cdnUrls = sessionService.getCdnUrl();
        const basePath = cdnUrls.content_cdn_url;
        const appRootPath = window.location.origin; // configuration appRootPath
        markers.forEach((item: any) => {
            const thumbnailUrl = item.thumbnail
                ? basePath + item.thumbnail
                : appRootPath + DEFAULT_IMAGES_PATH.marker;
            const markerItem: MarkerListModel = {
                authorEtlSecs: item.authorEtlSecs,
                name: item.name,
                description: item.description,
                grades: item.grades,
                id: item.id,
                markerType: MARKER_TYPES[item.markerType] || MARKER_TYPES,
                metadata: item.metadata || {},
                systemEtlSecs: item.systemEtlSecs,
                thumbnail: appRootPath + DEFAULT_IMAGES_PATH.marker,
                categoryCode: item.txCategoryCode,
                competencies: this.normalizeCompetencies(item.txCompetencies),
                domains: this.normalizeDomains(item.txDomains),
                framework: item.txFramework,
                subject: this.normalizeSubject(item.txSubject),
                topics: this.normalizeTopics(item.txTopics),
                highlineCompetencies: item.txGutTopicHighlineCompCodes,
                sequence: item.sequence,
            };
            markerList.push(markerItem);
        });
        const marker = {
            markerList,
            totalCount: payload.totalHitCount,
        };
        return marker;
    }

    public normalizeGeneratedRoute(payload: any) {
        payload = payload.learning_journey || [];
        const journeyData: RouteMilestoneModel[] = [];
        payload.forEach((item: any) => {
            const milestone: RouteMilestoneModel = {
                milestoneId: item.milestone_id,
                framework: item.framework,
                subject: item.subject,
                gradeId: item.grade_id,
                gradeName: item.grade_name,
                gradeSeq: item.grade_seq,
                lessons: this.normalizeRouteLessons(item.lessons),
            };
            journeyData.push(milestone);
        });
        return journeyData;
    }

    public normalizeMarkerHighline(domainList: any, payload: any) {
        const domains = payload.domains || [];
        const topics = payload.topics || [];
        const competencies = payload.competencies || [];
        const gradeLineBoundries: MarkerBoundaryModel[] = [];
        const highlineCompetencies = payload.highlineCompetencies || [];
        domainList.forEach((domain: Domain) => {
            const matchedDomain = domains.find(
                (item: MarkerDomainsModel) => item.domainDisplayCode === domain.domainCode);
            const domainCompCodes = highlineCompetencies.filter(
                (compCode: string) => domain.topics.find(
                    (topic) => topic.competencies!.find(
                        (compItem) => compItem.competencyCode === compCode)));
            if (matchedDomain) {
                domain.topics.forEach((topic) => {
                    const matchedTopic = topics.find((item: MarkerTopicModel) =>
                    item.topicDisplayCode === topic.topicCode);
                    const topicCompCodes = domainCompCodes.filter(
                        (compCode: string) => topic.competencies!.find((compItem) =>
                        compItem.competencyCode === compCode));
                    if (matchedTopic) {
                        (topic.competencies || []).forEach((competency) => {
                            const matchedCompetency = competencies.find(
                                (item: MarkerCompetencyModel) => item.competencyCode === competency.competencyCode);
                            const hasHighline = topicCompCodes.indexOf(competency.competencyCode) !== -1;
                            if (matchedCompetency && hasHighline) {
                                const createBoundary: MarkerBoundaryModel = {
                                    averageComp: competency.competencyCode,
                                    domainCode: domain.domainCode,
                                    highline: competency.competencyCode,
                                    highlineComp: competency.competencyCode,
                                    highlineTopic: topic.topicCode,
                                    topicAverageComp: competency.competencyCode,
                                    topicCode: topic.topicCode || '',
                                    topicHighlineComp: competency.competencyCode,
                                };
                                gradeLineBoundries.push(createBoundary);
                            }
                        });
                    }
                });
            }
        });
        return gradeLineBoundries;
    }

    public normalizeCollectionsData(payload: any) {
        const collectionSummary = payload.collection_summary || [];
        const collectionData: MarkerCollectionModel[] = [];
        const cdnUrls = sessionService.getCdnUrl();
        const basePath = cdnUrls.content_cdn_url;
        const appRootPath = window.location.origin;
        collectionSummary.forEach((collection: any) => {
            const thumbnailUrl = collection.thumbnail
                ? basePath + collection.thumbnail
                : appRootPath + DEFAULT_IMAGES_PATH[collection.format];
            const collectionModel: MarkerCollectionModel = {
                id: collection.id,
                gutCodes: collection.gut_codes,
                taskCount: collection.task_count,
                questionCount: collection.question_count,
                oeQuestionCount: collection.oe_question_count,
                resourceCount: collection.resource_count,
                title: collection.title,
                url: collection.url,
                format: collection.format,
                learningObjective: collection.learning_objective,
                sequenceId: collection.sequence_id,
                subformat: collection.subformat,
                thumbnailUrl,
            };
            collectionData.push(collectionModel);
        });
        sortByProperty(collectionData, 'sequenceId', 'ASC');
        return collectionData;
    }

    public normalizeSuggestions(payload: any) {
        const suggestions = payload.suggestions || [];
        const suggestedContent: SuggestionsModel[] = [];
        suggestions.forEach((item: any) => {
            const standards = taxonomySerializer.normalizeTaxonomyObject(item.taxonomy);
            const competency = standards[0] || {};
            const contents = item.contents ? this.normalizeSuggestionContent(item.contents) : null;
            const resourceFormat = contents ? contents[0].subFormat : null;
            const suggsestion: SuggestionsModel = {
                id: item.id,
                title: item.title,
                format: item.format,
                thumbnail: item.thumbnail,
                score: item.score,
                competencyCode: competency.code,
                standards,
                filterWeight: item.filterWeight,
                contents,
                resourceFormat,
                actionId: item.actionId,
            };
            suggestedContent.push(suggsestion);
        });
        sortByProperty(suggestedContent, 'score', 'DESC');
        return suggestedContent;
    }

    private normalizeSuggestionContent(payload: any) {
        const contents = payload || [];
        return contents.map((content: any) => {
            const item: SuggestionContentModel = {
                id: content.id,
                originalContentId: content.originalContentId,
                subFormat: content.contentSubFormat,
                format: content.contentFormat,
                title: content.title,
                url: content.url,
            };
            return item;
        });
    }

    private normalizeCompetencies(payload: any) {
        const competencies: MarkerCompetenciesModel[] = [];
        payload.forEach((item: any) => {
            const splitCompCode = item.txCompCode.split('.');
            const competencyCode = splitCompCode.slice(1, splitCompCode.length).join('.');
            const competency: MarkerCompetenciesModel = {
                compCode: item.txCompCode,
                competencyCode,
                compDisplayCode: item.txCompDisplayCode,
                compName: item.txCompName,
                shortName: item.txCompShortName,
            };
            competencies.push(competency);
        });
        return competencies;
    }

    private normalizeDomains(payload: any) {
        const domains: MarkerDomainsModel[] = [];
        payload.forEach((item: any) => {
            const domain: MarkerDomainsModel = {
                domainCode: item.txDomainCode,
                domainDisplayCode: (item.txDomainDisplayCode.split('-')).pop(),
                domainName: item.txDomainName,
            };
            domains.push(domain);
        });
        return domains;
    }

    private normalizeTopics(payload: any) {
        const domains: MarkerTopicModel[] = [];
        payload.forEach((item: any) => {
            const domain: MarkerTopicModel = {
                topicCode: item.txTopicCode,
                topicDisplayCode: item.txTopicDisplayCode,
                topicName: item.txTopicName,
            };
            domains.push(domain);
        });
        return domains;
    }

    private normalizeSubject(payload: any) {
        let subjectCode = (payload.txSubjectCode && payload.txSubjectCode.split('.')) || [];
        subjectCode = subjectCode.length > 2 ? subjectCode.slice(1, subjectCode.length) : subjectCode;
        return {
            subjectCode: subjectCode.join('.'),
            subjectDisplayCode: payload.txSubjectDisplayCode,
            subjectName: payload.txSubjectName,
        } as MarkerSubjectModel;
    }

    private normalizeRouteLessons(payload: any) {
        const lessons = payload || [];
        const lessonData: RouteLessonsModel[] = [];
        lessons.forEach((item: any) => {
            const diagnostic = item.diagnostic || null;
            const lesson: RouteLessonsModel = {
                lessonId: item.lesson_id,
                lessonTitle: item.lesson_title,
                compCode: item.tx_comp_code,
                compName: item.tx_comp_name,
                compStudentDesc: item.tx_comp_student_desc,
                compSeq: item.tx_comp_seq,
                domainCode: item.tx_domain_name,
                domainName: item.tx_domain_name,
                domainSeq: item.tx_domain_seq,
                compDisplayCode: item.tx_display_code,
                suggestions: this.normalizeSuggestedContent(item.suggestions),
                diagnostic: {
                    sessionId: diagnostic && diagnostic.session_id,
                    status: diagnostic && diagnostic.status,
                },
            };
            lessonData.push(lesson);
        });
        sortByProperty(lessonData, 'compSeq', 'ASC');
        sortByProperty(lessonData, 'domainSeq', 'ASC');
        return lessonData;
    }

    private normalizeSuggestedContent(payload: any) {
        const suggestions = payload || {};
        const suggestedContentData: RouteSuggestionModel = {};
        const signaturePayload = suggestions.signature || [];
        const dependentLessonPayload = suggestions.dependent_lesson || [];
        const diagnosticLessonPayload = suggestions.diagnostic_lesson || [];
        suggestedContentData.dependentLesson = this.normalizeAdditionalContent(dependentLessonPayload);
        suggestedContentData.diagnosticLesson = this.normalizeAdditionalContent(diagnosticLessonPayload);
        suggestedContentData.signature = this.normalizeAdditionalContent(signaturePayload);
        return suggestedContentData;
    }

    private normalizeAdditionalContent(payload: any) {
        const contentPayload = payload || [];
        const contentData: RouteSignatureModel[] = [];
        contentPayload.forEach((item: any) => {
            const contentItem: RouteSignatureModel = {
                id: item.id,
                ctxCollectionId: item.ctx_collection_id,
                suggestedContentId: item.suggested_content_id,
                suggestedContentType: item.suggested_content_type,
                suggestedContentSubTyle: item.suggested_content_subtype,
                thumbnail: item.thumbnail,
                title: item.title,
                sequenceId: item.sequence_id,
                questionCount: item.question_count,
                suggestions: this.normalizeSuggestedContent(item.suggestions),
            };
            contentData.push(contentItem);
        });
        return contentData;
    }

}

export const markerSerilaizer = MarkerSerializer.instance;
